import { ActionContext } from "vuex";
import {
  Course,
  CourseTrack,
  Instructor,
  School,
} from "../../../../domain/entities/entities";
import { InterfaceSchoolUseCases } from "../../../../domain/usecase/impl/schoolUseCases";
import { InterfaceCourseTrackUseCases } from "../../../../domain/usecase/impl/courseTrackUseCases";
import { InterfaceCourseUseCases } from "../../../../domain/usecase/impl/courseUseCases";
import searchInstructorsUseCase from "../../../../domain/usecase/impl/instructors/instructorUseCase";
import { Order } from "../../../../domain/entities/order";
import { ListResponse } from "../../../../domain/entities/shop/product/listResponse";

class GlobalSearchState {
  schools: School[] = Array<School>();
  courseTracks: CourseTrack[] = Array<CourseTrack>();
  courses: Course[] = Array<Course>();
  instructors: Instructor[] = Array<Instructor>();
  searching: boolean = false;
  coursesPage = 0;
  instructorCourses: Course[] = [];
  instructorsCoursesPage = 0;
}

const state = new GlobalSearchState();

const mutations = {
  LIST_SCHOOLS(state: GlobalSearchState, schools: School[]): void {
    state.schools = schools;
  },
  LIST_COURSE_TRACKS(
    state: GlobalSearchState,
    courseTracks: CourseTrack[]
  ): void {
    state.courseTracks = courseTracks;
  },
  SET_COURSES(state: GlobalSearchState, courses: Course[]): void {
    state.courses = courses;
  },
  SET_SEARCHING(state: GlobalSearchState, isSearching: boolean): void {
    state.searching = isSearching;
  },
  SET_COURSES_PAGE(state: GlobalSearchState, page: number): void {
    state.coursesPage = page;
  },
  SET_INSTRUCTORS(state: GlobalSearchState, instructors: Instructor[]): void {
    state.instructors = instructors;
  },
  SET_INSTRUCTOR_COURSES(
    state: GlobalSearchState,
    instructorCourses: Course[]
  ): void {
    state.instructorCourses = instructorCourses;
  },
  SET_INSTRUCTOR_COURSES_PAGE(
    state: GlobalSearchState,
    instructorsCoursesPage: number
  ): void {
    state.instructorsCoursesPage = instructorsCoursesPage;
  },
};

const actions = {
  async loadSchools(
    { commit }: ActionContext<GlobalSearchState, GlobalSearchState>,
    search: string
  ): Promise<void> {
    if (search?.length) {
      const schools = await InterfaceSchoolUseCases.listBySearch(search);
      commit("LIST_SCHOOLS", schools);
    } else {
      commit("LIST_SCHOOLS", []);
    }
  },

  async loadCourseTracks(
    { commit }: ActionContext<GlobalSearchState, GlobalSearchState>,
    search: string
  ): Promise<void> {
    if (search?.length) {
      const courseTracks = await InterfaceCourseTrackUseCases.listBySearch(
        search
      );
      commit("LIST_COURSE_TRACKS", courseTracks);
    } else {
      commit("LIST_COURSE_TRACKS", []);
    }
  },

  async loadCourses(
    { commit }: ActionContext<GlobalSearchState, GlobalSearchState>,
    order: Order
  ): Promise<void> {
    try {
      const listResponse: ListResponse<Course> = await InterfaceCourseUseCases.listBySearch(
        order
      );
      commit("SET_COURSES", listResponse.items);
      commit("SET_COURSES_PAGE", listResponse.page);
    } catch (e) {
      console.error(e);
      throw e;
    }
  },

  async loadInstructors(
    { commit }: ActionContext<GlobalSearchState, GlobalSearchState>,
    search: string
  ): Promise<void> {
    try {
      if (search?.length) {
        const response: Instructor[] = await searchInstructorsUseCase.listBySearchInstructors(
          search
        );
        commit("SET_INSTRUCTORS", response);
      } else {
        commit("SET_INSTRUCTORS", []);
      }
    } catch (e) {
      console.error(e);
      throw e;
    }
  },

  async getCoursesByInstructors(
    { commit }: ActionContext<GlobalSearchState, GlobalSearchState>,
    payload: { order: Order; instructorID: number }
  ): Promise<void> {
    try {
      const response: ListResponse<Course[]> = await searchInstructorsUseCase.getCoursesByInstructors(
        payload.order,
        payload.instructorID
      );
      commit("SET_INSTRUCTOR_COURSES", response.items);
      commit("SET_INSTRUCTOR_COURSES_PAGE", response.page);
    } catch (e) {
      console.log(e);
      throw e;
    }
  },

  async setSearch(
    { commit }: ActionContext<GlobalSearchState, GlobalSearchState>,
    isSearching: boolean
  ): Promise<void> {
    commit("SET_SEARCHING", isSearching);
  },
};

const getters = {
  schools: (state: GlobalSearchState): School[] => state.schools,
  courseTracks: (state: GlobalSearchState): CourseTrack[] => state.courseTracks,
  courses: (state: GlobalSearchState): Course[] => state.courses,
  instructors: (state: GlobalSearchState): Instructor[] => state.instructors,
  searching: (state: GlobalSearchState): boolean => state.searching,
  coursesPage: (state: GlobalSearchState): number => state.coursesPage,
  instructorCourses: (state: GlobalSearchState): Course[] =>
    state.instructorCourses,
  instructorsCoursesPage: (state: GlobalSearchState): number =>
    state.instructorsCoursesPage,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
